/**
 * Order Timer Component
 * Shows color-coded timer for order elapsed time
 * Caps display at 2 hours and shows "STALE" indicator for very old orders
 */
import { useState, useEffect } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';

// Timer thresholds in minutes
const THRESHOLDS = {
  takeaway: { yellow: 5, orange: 10, red: 10 },
  'dine-in': { yellow: 10, orange: 15, red: 15 }
};

// Cap display at 2 hours (120 minutes) - orders older than this are "stale"
const STALE_THRESHOLD_MINUTES = 120;

export default function OrderTimer({ createdAt, orderType = 'dine-in', size = 'default' }) {
  const [elapsed, setElapsed] = useState(0);
  const [color, setColor] = useState('green');
  const [flashing, setFlashing] = useState(false);
  const [isStale, setIsStale] = useState(false);

  useEffect(() => {
    const updateTimer = () => {
      if (!createdAt) return;
      
      const created = new Date(createdAt);
      const now = new Date();
      const elapsedMs = now - created;
      const elapsedMin = Math.floor(elapsedMs / 60000);
      
      // Cap display at stale threshold
      if (elapsedMin >= STALE_THRESHOLD_MINUTES) {
        setIsStale(true);
        setElapsed(STALE_THRESHOLD_MINUTES);
        setColor('red');
        setFlashing(true);
        return;
      }
      
      setIsStale(false);
      setElapsed(elapsedMin);
      
      // Determine color based on order type
      const thresholds = THRESHOLDS[orderType] || THRESHOLDS['dine-in'];
      
      if (elapsedMin >= thresholds.red) {
        setColor('red');
        setFlashing(true);
      } else if (elapsedMin >= thresholds.orange) {
        setColor('orange');
        setFlashing(false);
      } else if (elapsedMin >= thresholds.yellow) {
        setColor('yellow');
        setFlashing(false);
      } else {
        setColor('green');
        setFlashing(false);
      }
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [createdAt, orderType]);

  const colorClasses = {
    green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'
  };

  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
  };

  const sizeClasses = {
    small: 'px-2 py-1 text-xs',
    default: 'px-3 py-1.5 text-sm',
    large: 'px-4 py-2 text-base'
  };

  // Show stale indicator for very old orders
  if (isStale) {
    return (
      <div
        className={`
          inline-flex items-center gap-1.5 rounded-full border font-bold
          bg-red-500 text-white border-red-600 animate-pulse
          ${sizeClasses[size]}
        `}
        title="Order is over 2 hours old - consider cancelling"
      >
        <AlertTriangle className={size === 'small' ? 'w-3 h-3' : size === 'large' ? 'w-5 h-5' : 'w-4 h-4'} />
        <span>STALE 2h+</span>
      </div>
    );
  }

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 rounded-full border font-medium
        ${colorClasses[color]}
        ${sizeClasses[size]}
        ${flashing ? 'animate-pulse' : ''}
      `}
    >
      <Clock className={size === 'small' ? 'w-3 h-3' : size === 'large' ? 'w-5 h-5' : 'w-4 h-4'} />
      <span>{formatTime(elapsed)}</span>
    </div>
  );
}

export { THRESHOLDS, STALE_THRESHOLD_MINUTES };
